import {
  ContentHeader,
  EmptyState,
  Progress,
  SupportButton,
} from '@backstage/core-components';
import { Button, Typography, makeStyles } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import { PortfolioCreationEntry } from './PortfolioCreationEntry';
import { ActivityFiltersPanel } from './ActivityFiltersPanel';
import { AdminDeletionPanel } from './AdminDeletionPanel';
import { PortfolioTable } from './PortfolioTable';
import type { PortfolioColumnContext } from './portfolioColumns';
import type { PortfolioFilters as PortfolioFilterState } from './usePortfolioFilters';
import type { PortfolioDeletion } from './usePortfolioDeletion';
import type { usePortfolioQueries } from './usePortfolioQueries';

export interface PortfolioCopy {
  portfolioTitle: string;
  portfolioSubtitle: string;
  supportText: string;
  emptyDescription: string;
}

interface PortfolioContentProps extends PortfolioColumnContext {
  copy: PortfolioCopy;
  filterState: PortfolioFilterState;
  deletion: PortfolioDeletion;
  queries: ReturnType<typeof usePortfolioQueries>;
}

const useStyles = makeStyles(theme => ({
  loadingSkeleton: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gap: theme.spacing(1),
  },
  actionInfo: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

export function PortfolioError({
  error,
  onRetry,
}: {
  error: Error;
  onRetry: () => void;
}) {
  return (
    <EmptyState
      title="Erro ao carregar atividades"
      description={error.message}
      missing="info"
      action={
        <Button variant="contained" color="primary" onClick={onRetry}>
          Tentar novamente
        </Button>
      }
    />
  );
}

function PortfolioFilters({
  filterState,
  queries,
  labels,
  selection,
  deletion,
  access,
}: PortfolioContentProps) {
  return (
    <ActivityFiltersPanel
      filters={filterState.filters}
      options={queries.filterOptions}
      loadingOptions={queries.loadingFilterOptions}
      getProjectLabel={labels.getProjectLabel}
      getCreatorLabel={labels.getUserLabel}
      onFiltersChange={patch => {
        selection.clear();
        filterState.change(patch);
      }}
      onReset={() => {
        selection.clear();
        filterState.reset();
      }}
      resetBusy={deletion.busy}
      administrativeActions={
        <AdminDeletionPanel
          isGlobalAdmin={access.isGlobalAdmin}
          selectedIds={selection.selectedIds}
          selectedTargets={selection.selectedTargets}
          busy={queries.loading || deletion.busy}
          batch={deletion.batch}
          onRequestBatch={() => void deletion.requestBatch()}
          onOverride={id => void deletion.overrideCleanup(id)}
          onClearSelection={selection.clear}
        />
      }
    />
  );
}

function PortfolioLoading() {
  const classes = useStyles();
  return (
    <>
      <Progress />
      <div
        className={classes.loadingSkeleton}
        aria-label="Carregando atividades"
      >
        <Skeleton variant="rect" height={36} />
        <Skeleton variant="rect" height={36} />
        <Skeleton variant="rect" height={36} />
        <Skeleton variant="rect" height={36} />
      </div>
    </>
  );
}

function PortfolioResults(props: PortfolioContentProps) {
  const { queries, copy, access } = props;
  if (queries.loading) return <PortfolioLoading />;
  if (queries.activities.length === 0) {
    return (
      <EmptyState
        title="Nenhuma atividade encontrada"
        description={copy.emptyDescription}
        missing="content"
        action={
          <PortfolioCreationEntry
            state={access.creationState}
            onCreate={access.handleCreate}
            onRetry={access.refreshRoles}
          />
        }
      />
    );
  }
  return <PortfolioTable {...props} />;
}

export function PortfolioContent(props: PortfolioContentProps) {
  const classes = useStyles();
  const { copy, access, deletion } = props;
  return (
    <>
      <ContentHeader title="Atividades">
        <SupportButton>{copy.supportText}</SupportButton>
        <PortfolioCreationEntry
          state={access.creationState}
          onCreate={access.handleCreate}
          onRetry={access.refreshRoles}
        />
      </ContentHeader>
      <PortfolioFilters {...props} />
      {deletion.actionInfo && (
        <Typography variant="body2" className={classes.actionInfo}>
          {deletion.actionInfo}
        </Typography>
      )}
      <PortfolioResults {...props} />
    </>
  );
}
